"use client"

import { Head } from "@inertiajs/react"
import { Plus } from "@untitledui/icons"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { CopyLinkButton } from "@/components/warm-starts/copy-link-button"
import { WarmStartLedgerTable } from "@/components/warm-starts/ledger-table"
import { NewInviteModal } from "@/components/warm-starts/new-invite-modal"
import { DashboardLayout } from "@/layouts/dashboard-layout"
import { newAdminWarmStartPath } from "@/routes"
import type { WarmStartLedgerRow } from "@/types"

interface IndexProps {
  warm_starts: WarmStartLedgerRow[]
  ready_id: number | null
}

export default function WarmStartsIndex({ warm_starts, ready_id }: IndexProps) {
  const ready = warm_starts.find((row) => row.id === ready_id)
  const [composing, setComposing] = useState(false)

  return (
    <DashboardLayout>
      <Head title="Warm Invites" />
      <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 pt-8 pb-12 lg:px-8">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-col gap-0.5">
            <h1 className="text-primary font-display md:text-display-xs text-xl font-semibold">
              Warm Invites
            </h1>
            <p className="text-md text-tertiary">
              Invites and where each one is in the funnel.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button color="secondary" size="md" href={newAdminWarmStartPath()}>
              Compose in full
            </Button>
            <Button
              size="md"
              iconLeading={Plus}
              onClick={() => setComposing(true)}
            >
              New invite
            </Button>
          </div>
        </header>

        <NewInviteModal isOpen={composing} onOpenChange={setComposing} />

        {ready && (
          <ReadyLinkCard url={ready.claim_url} name={ready.recipient_name} />
        )}

        {warm_starts.length === 0 ? (
          <EmptyState size="sm" className="py-10">
            <EmptyState.Header pattern="none">
              <EmptyState.FeaturedIcon
                color="gray"
                theme="modern"
                icon={Plus}
              />
            </EmptyState.Header>
            <EmptyState.Content>
              <p className="text-primary text-md text-center font-semibold">
                No invites yet
              </p>
              <EmptyState.Description>
                Compose one to turn a conversation into a published artifact.
              </EmptyState.Description>
            </EmptyState.Content>
            <EmptyState.Footer>
              <Button
                size="md"
                iconLeading={Plus}
                onClick={() => setComposing(true)}
              >
                New invite
              </Button>
            </EmptyState.Footer>
          </EmptyState>
        ) : (
          <WarmStartLedgerTable rows={warm_starts} aria-label="Invites" />
        )}
      </main>
    </DashboardLayout>
  )
}

function ReadyLinkCard({ url, name }: { url: string; name: string }) {
  return (
    <div className="border-brand bg-brand-primary flex flex-col gap-3 rounded-2xl border p-5">
      <div className="flex flex-col gap-0.5">
        <p className="text-primary text-md font-semibold">
          {name}&rsquo;s link is ready
        </p>
        <p className="text-tertiary text-sm">
          Send it to them — clicking it signs them in and drops them onto their
          draft.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <code className="text-secondary bg-primary border-secondary min-w-0 flex-1 truncate rounded-lg border px-3 py-2 text-sm">
          {url}
        </code>
        <CopyLinkButton url={url} withLabel />
      </div>
    </div>
  )
}
